import { useToast } from '@chakra-ui/react';
import { getDeploymentRegenerateUrl } from '../routes';
import { getErrorMessage, logError } from './errorUtils';
import { getDeploymentInfoSync } from './regenerationValidation';
import {
  SitecoreContextResourceType,
  TenantApplicationNode,
  RegenerationErrorType,
} from '../types/types';

export const handleRegenerateInNewTab = async (
  sitecoreContextId: string,
  resources: SitecoreContextResourceType[] | undefined,
  applicationsNodes: TenantApplicationNode[],
  toast: ReturnType<typeof useToast>,
): Promise<void> => {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    let organizationId = searchParams.get('organization');

    if (!organizationId) {
      const pathParts = window.location.pathname.split('/');
      for (let i = 0; i < pathParts.length; i++) {
        if (pathParts[i] === 'organization' && i + 1 < pathParts.length) {
          organizationId = pathParts[i + 1];
          break;
        }
      }
    }

    if (!organizationId && resources && resources.length > 0) {
      const contextResource = resources.find((r) => r.organizationId);
      if (contextResource && contextResource.organizationId) {
        organizationId = contextResource.organizationId;
      }
    }

    if (!organizationId) {
      organizationId =
        localStorage.getItem('organizationId') ||
        document.cookie
          .split('; ')
          .find((row) => row.startsWith('organizationId='))
          ?.split('=')[1] ||
        null;
    }

    if (!organizationId) {
      toast({
        status: 'error',
        title: 'Missing Organization ID',
        description: getErrorMessage(
          RegenerationErrorType.MISSING_ORGANIZATION_ID,
        ),
        isClosable: false,
        duration: 5000,
      });
      return;
    }

    const formattedOrgId = organizationId.startsWith('org_')
      ? organizationId
      : 'org_' + organizationId;
    const deploymentInfo = getDeploymentInfoSync(resources, applicationsNodes);

    if (!deploymentInfo) {
      toast({
        status: 'error',
        title: 'Regeneration Error',
        description: getErrorMessage(RegenerationErrorType.NO_ANNOTATIONS),
        isClosable: false,
        duration: 5000,
      });
      return;
    }

    const { projectId, environmentId } = deploymentInfo;

    if (!projectId || !environmentId) {
      let errorType = RegenerationErrorType.GENERAL_ERROR;

      if (!projectId && !environmentId) {
        errorType = RegenerationErrorType.NO_ANNOTATIONS;
      } else if (!projectId) {
        errorType = RegenerationErrorType.MISSING_PROJECT_ID;
      } else if (!environmentId) {
        errorType = RegenerationErrorType.MISSING_ENVIRONMENT_ID;
      }

      toast({
        status: 'error',
        title: 'Regeneration Error',
        description: getErrorMessage(errorType),
        isClosable: false,
        duration: 5000,
      });
      return;
    }

    const deployUrl = getDeploymentRegenerateUrl(
      projectId,
      environmentId,
      formattedOrgId,
    );

    const newTab = window.open('about:blank', '_blank');
    if (newTab) {
      newTab.location.href = deployUrl;
      newTab.focus();
    } else {
      toast({
        status: 'warning',
        title: 'Pop-up Blocked',
        description:
          'Please enable pop-ups for this site to open regeneration in a new tab.',
        isClosable: false,
        duration: 5000,
      });
    }
  } catch (error) {
    logError('handleRegenerateInNewTab', error, { sitecoreContextId });
    toast({
      status: 'error',
      title: 'Regeneration Error',
      description:
        'An unexpected error occurred while processing the regeneration request.',
      isClosable: false,
      duration: 5000,
    });
  }
};
